import { supabase } from '@/integrations/supabase/client';
import { getStaffSessionToken, handleInvalidSession, isInvalidSessionError } from '@/services/staffSession';

/**
 * "Finishing soon": patient_seen cases the automatic-close job will close as Referred Back on a set
 * date unless staff press Keep open or the patient returns. The job, the thresholds and the
 * definition of "eligible" all live in the database (auto_close_candidates,
 * auto_status_settings, migration 20260929142000); this file only reads and vetoes, so the chip and
 * the job cannot disagree. Nothing here holds a threshold.
 */
export interface FinishingSoonRow {
  case_id: string;
  referring_doctor_id: string | null;
  referring_doctor: string | null;
  last_visit_date: string; // YYYY-MM-DD, last finalized Plato visit
  eligible_since: string; // YYYY-MM-DD
  closes_on: string; // YYYY-MM-DD, the day the daily job closes it
}

export interface AutoStatusLastRun {
  run_at: string;
  advanced: number;
  flagged: number;
  closed: number;
  cleared: number;
  errors: number;
}

export interface FinishingSoonDoctorGroup {
  key: string;
  label: string;
  count: number;
  earliestClosesOn: string;
}

export const UNKNOWN_DOCTOR_LABEL = 'Unknown doctor';

// Cast: these RPCs are new (migration 20260930140000) and not yet in the generated Supabase types.
// All three take the staff session token: the public key alone can call none of them.
type Rpc = (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { code?: string; message: string } | null }>;
const rpc = async (fn: string, args: Record<string, unknown>): Promise<unknown> => {
  const { data, error } = await (supabase.rpc as unknown as Rpc)(fn, args);
  if (error) {
    if (isInvalidSessionError(error)) handleInvalidSession();   // dead token: back to the login screen
    throw error;
  }
  return data;
};

export const getFinishingSoonCases = async (): Promise<FinishingSoonRow[]> => {
  const token = getStaffSessionToken();
  if (!token) return [];   // no staff session in this browser: nothing to show, and nothing leaks
  return ((await rpc('get_finishing_soon_cases_by_session', { p_session_token: token })) ?? []) as FinishingSoonRow[];
};

export const getAutoStatusLastRun = async (): Promise<AutoStatusLastRun | null> => {
  const token = getStaffSessionToken();
  if (!token) return null;
  const rows = ((await rpc('get_auto_status_last_run_by_session', { p_session_token: token })) ?? []) as AutoStatusLastRun[];
  return rows[0] ?? null;
};

/**
 * Keep open: the automatic close never closes this case again. Who pressed it is taken from the
 * staff session on the server, never sent from here.
 */
export const keepCaseOpen = async (caseId: string): Promise<void> => {
  const token = getStaffSessionToken();
  if (!token) {
    handleInvalidSession();
    throw new Error('Your session has ended. Please sign in again.');
  }
  await rpc('keep_case_open_by_session', { p_session_token: token, p_case_id: caseId });
};

export const doctorKey = (row: Pick<FinishingSoonRow, 'referring_doctor_id' | 'referring_doctor'>): string =>
  row.referring_doctor_id ?? row.referring_doctor?.trim() ?? UNKNOWN_DOCTOR_LABEL;

/** n per referring doctor, biggest first, then by the date the first of their cases closes. */
export const groupByDoctor = (rows: FinishingSoonRow[]): FinishingSoonDoctorGroup[] => {
  const groups = new Map<string, FinishingSoonDoctorGroup>();
  for (const row of rows) {
    const key = doctorKey(row);
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      if (row.closes_on < existing.earliestClosesOn) existing.earliestClosesOn = row.closes_on;
    } else {
      groups.set(key, {
        key,
        label: row.referring_doctor?.trim() || UNKNOWN_DOCTOR_LABEL,
        count: 1,
        earliestClosesOn: row.closes_on,
      });
    }
  }
  return [...groups.values()].sort(
    (a, b) => b.count - a.count || a.earliestClosesOn.localeCompare(b.earliestClosesOn) || a.label.localeCompare(b.label)
  );
};
